import { DictionaryContext } from '../../../context/Dictionary';
import { useCallback, useContext, useEffect, useState } from 'react';
import { AttentionCard } from 'shared';
import errorIcon from '../../../../public/icons/icon_alert.svg';
import successIcon from '../../../../public/icons/icon_yes.svg';
import infoIcon from '../../../../public/icons/pictogram_telephone_info.svg';
import { _CallMeBackForm, _Errors } from '../../../dictionaryItems';
import { extractTime, formatTimeOption } from '../../../utilities/date';
import { getOpeningHours, OpeningHoursProps, registerCallBack, RequestState } from '../../../utilities/requestCallBack';
import { CallBackForm, Option } from './CallBackForm';

export interface CallBackFormPropsAPI {
	callSubjectList: string[];
	bankList: string[];
	days: number;
	descriptionText?: string;
	title?: string;
}

export const CallBackFormAPI: React.FC<CallBackFormPropsAPI> = ({
	days,
	callSubjectList,
	bankList,
	descriptionText,
	title,
}) => {
	const [openingHours, setOpeningHours] = useState<OpeningHoursProps[]>(null);
	const [timeOptions, setTimeOptions] = useState<Option[]>(null);
	const [selectedDate, setSelectedDate] = useState<OpeningHoursProps>(null);
	const [requestState, setRequestState] = useState<RequestState>(null);
	const dictionary = useContext(DictionaryContext);

	useEffect(() => {
		const fetchOpeningHours = async () => {
			const response = await getOpeningHours(days);

			setOpeningHours(response);
		};
		fetchOpeningHours();
	}, [days]);

	const handleSelectedDateIndex = useCallback(
		(dateIndex: string) => {
			if (!openingHours) return;
			const selectedDate = openingHours[dateIndex];
			const options = [];
			let sessionOpening = extractTime(selectedDate.open);
			const sessionClosing = extractTime(selectedDate.close);
			while (sessionOpening < sessionClosing) {
				options.push({
					value: formatTimeOption(sessionOpening),
					text: formatTimeOption(sessionOpening) + ' - ' + formatTimeOption(sessionOpening + 1),
				});
				sessionOpening++;
			}
			options[0].selected = true;
			setTimeOptions(options);
			setSelectedDate(selectedDate);
		},
		[openingHours],
	);

	useEffect(() => {
		handleSelectedDateIndex('0');
	}, [handleSelectedDateIndex, openingHours]);

	const getCallSubjectList = (): Option[] => {
		return callSubjectList.map((subject) => {
			return { value: subject, text: subject };
		});
	};

	const getBankList = (): Option[] => {
		let bankListSafe = bankList;
		// TODO Remove fallback when CMS is updated (05-01-2026)
		if (!bankListSafe || bankListSafe.length === 0) {
			bankListSafe = [
				'Andelskassen Fælleskassen',
				'Arbejdernes Landsbank',
				'Dragsholm Sparekasse',
				'Faster/Oikos Andelskasse',
				'Frørup Andelskasse',
				'Fynske Bank',
				'Hvidbjerg Bank',
				'Klim Sparekasse',
				'Kreditbanken',
				'Merkur Andelskasse',
				'Nordfyns Bank',
				'Nordjyske Bank',
				'Nykredit',
				'PrivatSikring',
				'Ringkjøbing Landbobank',
				'Rise Sparekasse',
				'Rønde Sparekasse',
				'Skjern Bank',
				'Spar Nord Bank',
				'Sparekassen Balling',
				'Sparekassen Bredebro',
				'Sparekassen for Nr. Nebel & Om',
				'Sparekassen Kronjylland',
				'Sparekassen Thy',
				'Sydjysk Sparekasse',
				'Vestjysk Bank',
			];
		}
		const bankListOptions = bankListSafe.map((bankName) => {
			const name = bankName?.trim();
			return { value: name, text: name };
		});
		return bankListOptions.sort((a, b) => {
			return a.value.localeCompare(b.value);
		});
	};

	const handleRegisterCallBack = async (formData) => {
		const result = await registerCallBack(formData, selectedDate);
		setRequestState(result);
	};

	const handleAttentionCardText = (): string => {
		if (requestState == 'error') return _Errors(dictionary, 'IssuesWithOneOfOurSystems');
		if (requestState == 'info') return _CallMeBackForm(dictionary, 'AlreadyRegisteredNumber');
		return _CallMeBackForm(dictionary, 'YouWillBeCalledWithinTheDesiredTime');
	};

	const handleAttentionCardIcon = (): Image => {
		if (requestState == 'error') return { url: errorIcon };
		if (requestState == 'info') return { url: infoIcon };
		return { url: successIcon };
	};

	return requestState ? (
		<AttentionCard
			conditionsText={handleAttentionCardText()}
			cardType={{ type: requestState }}
			cardSize={{ size: 'medium' }}
			image={handleAttentionCardIcon()}
		/>
	) : (
		<CallBackForm
			bankList={getBankList()}
			callSubjectList={getCallSubjectList()}
			descriptionText={descriptionText}
			openingHours={openingHours}
			registerCallBack={(formData) => handleRegisterCallBack(formData)}
			selectedDate={selectedDate}
			handleSelectedDateIndex={handleSelectedDateIndex}
			timeOptions={timeOptions}
			title={title}
		/>
	);
};
